import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../../../context/ThemeContext";
import type { Driver } from "../../../../../store/admin/driversStore";
import type { ThemeClasses } from "../../../../../types/components";
import useIsMonitor from "../../../../../hooks/useIsMonitor";
import { activeThemeId } from "../../../../../config/theme";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";

const isChatEnabled = activeThemeId === "central-gps";

export default function DriverCard({
  driver,
  themeClasses,
  getStatusText,
  getStatusColor,
  handleOpenEditModal,
  setSelectedDriver,
  setShowDetailModal,
  onStartChat,
  isStartingChat = false,
  isCompanySelected,
  onDelete,
}: {
  driver: Driver;
  themeClasses: ThemeClasses;
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
  handleOpenEditModal: (driver: Driver) => void;
  setSelectedDriver: (driver: Driver) => void;
  setShowDetailModal: (show: boolean) => void;
  onStartChat?: (driver: Driver) => void;
  isStartingChat?: boolean;
  isCompanySelected: boolean;
  onDelete?: (driver: Driver) => void;
}) {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation("adminSections");

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const hasProfileImg = driver.profileImg && driver.profileImg.trim() !== "";

  const { isMonitor, isReadOnly } = useIsMonitor();
  return (
    <div
      key={driver.id}
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all`}
    >
      {/* Driver Header */}
      <div className="flex items-center space-x-4 mb-4">
        {hasProfileImg ? (
          <img
            src={driver.profileImg}
            alt={`${driver.firstName} ${driver.lastName}`}
            className={`w-16 h-16 rounded-full object-cover border-2 flex-shrink-0 ${
              isDarkMode ? "border-dark-border" : "border-gray-200"
            }`}
          />
        ) : (
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-primary-500 flex-shrink-0">
            <span className="text-xl font-bold text-gray-900">
              {getInitials(driver.firstName, driver.lastName)}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3
            className={`font-semibold ${themeClasses.text} text-lg line-clamp-2`}
          >
            {driver.firstName} {driver.lastName}
          </h3>

          {/* REEMPLAZAR POR CÓDIGO QR */}
          {/* <p className={`text-sm ${themeClasses.textSecondary}`}>
                        {driver.userId}
                      </p> */}
          {/* <QRGenerator id={driver.userId} size={60} /> */}
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getStatusColor(
            (driver as any).status_id || driver.driverStatus,
          )}`}
        >
          {getStatusText((driver as any).status_id || driver.driverStatus)}
        </span>
      </div>

      {/* Driver Details */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("drivers.fields.license")}:
          </span>
          <span className={`text-sm ${themeClasses.text} font-mono`}>
            {(driver as any).licenseId || driver.license_id}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("drivers.fields.vehicle")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {driver.vehicle_id || driver.vehicleId || t("drivers.fields.noVehicle")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("drivers.fields.days")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>{driver.days}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("drivers.fields.hours")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>{driver.hours}</span>
        </div>
      </div>

      {/* Contactos de emergencia (opcional) */}
      {driver.contactsEmergency && driver.contactsEmergency.length > 0 && (
        <div
          className={`space-y-2 mb-4 p-3 bg-gray-50 ${
            isDarkMode ? "dark:bg-[#303030]" : "dark:bg-gray-100"
          } rounded-lg`}
        >
          <span
            className={`block text-xs font-semibold ${themeClasses.textSecondary}`}
          >
            {t("drivers.fields.emergencyContacts")}:
          </span>
          {driver.contactsEmergency.map((contact, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <i
                className={`ri-phone-line text-sm ${themeClasses.textSecondary}`}
              ></i>
              <span className={`text-sm ${themeClasses.text}`}>
                {contact.name} - {contact.phone}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex space-x-2 mb-3">
        {!isMonitor && !isReadOnly && (
          <CRUDTooltip className="flex-1">
            <button
              className={`w-full py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors ${
                !isCompanySelected ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={() => handleOpenEditModal(driver)}
              disabled={!isCompanySelected}
            >
              {t("drivers.actions.edit")}
            </button>
          </CRUDTooltip>
        )}
        <button
          onClick={() => {
            setSelectedDriver(driver);
            setShowDetailModal(true);
          }}
          className="py-2 px-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors flex-1"
        >
          {t("drivers.actions.viewProfile")}
        </button>
        {isChatEnabled && onStartChat && (
          <button
            type="button"
            onClick={() => onStartChat(driver)}
            disabled={!driver.userId || isStartingChat}
            title={
              driver.userId
                ? "Iniciar chat con este conductor"
                : "Este conductor no tiene cuenta de usuario"
            }
            className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${
              !driver.userId || isStartingChat
                ? "opacity-50 cursor-not-allowed"
                : `${themeClasses.buttonHover} hover:text-primary-500`
            } transition-colors`}
          >
            <i
              className={`${
                isStartingChat
                  ? "ri-loader-4-line animate-spin"
                  : "ri-chat-3-line"
              } text-base`}
            ></i>
          </button>
        )}
        {onDelete && !isMonitor && !isReadOnly && (
          <button
            type="button"
            onClick={() => onDelete(driver)}
            title={t("drivers.actions.delete", "Eliminar")}
            className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/10 hover:bg-red-500/20 text-red-500 transition-colors"
          >
            <i className="ri-delete-bin-line text-base"></i>
          </button>
        )}
      </div>
    </div>
  );
}
